import { Fragment, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { MultiSelect } from '@components/MultiSelect';
import PhoneInput from '@components/PhoneInput';
import { Typography } from '@components/Typography';
import Autocomplete from '@components/Autocomplete';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { accountActions, alertActions } from '@actions';
import { authService } from '@services';
import { ROLE, ACCESS_LEVEL } from '@constants/user';
import { handleApiError } from '@helpers/api';
import { setInitialPhoneNumberToForm, preparePhoneNumberForSubmit } from '@helpers/phone';
import { Modal, Content, StyledRadioGroup } from './styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
};

const EditModal = ({ selectedUser, setSelectedUser, modal, setModal, brands, account }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const dispatch = useDispatch();
    const { mapSelectedLocationsToFormValue, getLocationsOptions } = useLocations();

    const UserSchema = yup.object().shape(
        {
            firstName: yup.string().required('validation.required'),
            email: yup.string().required('validation.required').email('validation.wrongEmailFormat'),
            phoneNumber: yup.string().validatePhone('validation.wrongPhoneFormat', true),
            role: yup.string().required('validation.required'),
            accessLevel: yup.string(),
            brands: yup.array().when(['accessLevel', 'role'], {
                is: (accessLevel, role) => accessLevel === `${ACCESS_LEVEL.BRAND}` && role !== `${ROLE.SUPER_ADMIN}`,
                then: yup.array().min(1, 'validation.required'),
                otherwise: yup.array(),
            }),
            locations: yup.array().when(['accessLevel', 'role'], {
                is: (accessLevel, role) => accessLevel === `${ACCESS_LEVEL.LOCATION}` && role !== `${ROLE.SUPER_ADMIN}`,
                then: yup.array().min(1, 'validation.required'),
                otherwise: yup.array(),
            }),
        },
        [['role', 'accessLevel']],
    );

    const resolver = useYupValidationResolver(UserSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting, dirtyFields, touchedFields },
    } = useForm({
        mode: 'all',
        resolver: data => resolver(data, { dirtyFields, touchedFields }),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            role: '',
            accessLevel: '0',
            brands: [],
            locations: [],
        },
    });

    const accessLevel = watch('accessLevel');
    const role = watch('role');

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));

    const locationsOptions = useMemo(() => getLocationsOptions(), []);

    useEffect(() => {
        setValue('phoneNumber', setInitialPhoneNumberToForm(selectedUser?.phoneNumber, account?.locale), {
            shouldValidate: !!selectedUser?.phoneNumber,
            shouldTouch: true,
        });
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('firstName', selectedUser?.firstName || '', { shouldValidate: true });
            setValue('lastName', selectedUser?.lastName || '');
            setValue('email', selectedUser?.email || '', { shouldValidate: true });

            setValue('role', selectedUser?.role, { shouldValidate: true });
            setValue('accessLevel', `${selectedUser?.accessLevel || 0}`, { shouldValidate: true });
            setValue('brands', selectedUser?.brands || [], { shouldValidate: true });
            setValue('locations', mapSelectedLocationsToFormValue(selectedUser?.locations) || [], {
                shouldValidate: true,
            });
        }
    }, [selectedUser, modal.type, setValue]);

    const onClose = () => {
        setModal({ open: false, type: FORM_TYPE.NEW });
        setSelectedUser(null);
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: { success: t('alertMessages.editSuccess'), error: t('alertMessages.editFail') },
        [FORM_TYPE.NEW]: { success: t('alertMessages.createSuccess'), error: t('alertMessages.createFail') },
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await authService.updateUser({
                ...values,
                locations: values.locations.map(location => location.id),
                phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber, true),
                accessLevel: Number(values.accessLevel),
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedUser.id }),
            });
            await dispatch(accountActions.get());
            dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
            onClose();
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error, { email: values.email });
        }
    };

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={modal.type === FORM_TYPE.NEW ? t('buttons.addUser') : t('SettingsUsers.editUser')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <Content>
                <Typography
                    variant="subtitle1"
                    sx={{ color: 'rgba(0, 0, 0, 0.87)', gridColumn: !isMobile && '1 / span 2' }}
                >
                    {t('SettingsUsers.generalInfo')}
                </Typography>
                <Controller
                    control={control}
                    name="firstName"
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            inputProps={{ ...field }}
                            label={t('common.firstName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="lastName"
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.lastNameOpt')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.email')}
                            fullWidth
                            disabled={!!selectedUser}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error } }) => (
                        <PhoneInput
                            placeholder=""
                            label={t('common.phoneNumberOpt')}
                            inputProps={{ name: 'phoneNumber' }}
                            value={field.value}
                            autoFormat
                            onChange={value => {
                                setValue('phoneNumber', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                    shouldTouch: true,
                                });
                            }}
                            error={error ? t(error.message) : null}
                        />
                    )}
                />
                <div style={{ gridColumn: !isMobile && '1 / span 2' }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: 'rgba(0, 0, 0, 0.87)',
                            marginBottom: '4px',
                        }}
                    >
                        {t('SettingsUsers.permissions')}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(0, 0, 0, 0.6)',
                            display: 'inline-block',
                            marginBottom: '24px',
                        }}
                    >
                        {t('SettingsUsers.permissionsDescription')}
                    </Typography>
                    <Controller
                        control={control}
                        name="role"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                onChange={({ target: { value } }) => {
                                    setValue('role', value, { shouldDirty: true, shouldValidate: true });
                                    if (value === ROLE.SUPER_ADMIN) {
                                        setValue('accessLevel', `${ACCESS_LEVEL.GLOBAL}`, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }
                                }}
                                options={[
                                    { value: ROLE.SUPER_ADMIN, label: t('SettingsUsers.superAdmin') },
                                    { value: ROLE.ADMIN, label: t('SettingsUsers.admin') },
                                    { value: ROLE.USER, label: t('SettingsUsers.user') },
                                ]}
                                label={t('SettingsUsers.role')}
                                fullWidth
                                required
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}
                            />
                        )}
                    />
                    {role !== ROLE.SUPER_ADMIN && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'rgba(0, 0, 0, 0.6)',
                                display: 'inline-block',
                                margin: '8px 0 0',
                            }}
                        >
                            {t('SettingsUsers.accessDescription')}
                        </Typography>
                    )}
                </div>
                {role !== ROLE.SUPER_ADMIN && (
                    <Controller
                        control={control}
                        name="accessLevel"
                        render={({ field }) => (
                            <div style={{ gridColumn: !isMobile && '1 / span 2' }}>
                                {[
                                    { id: `${ACCESS_LEVEL.GLOBAL}`, name: t('SettingsUsers.globalAccess') },
                                    { id: `${ACCESS_LEVEL.BRAND}`, name: t('SettingsUsers.brandAccess') },
                                    { id: `${ACCESS_LEVEL.LOCATION}`, name: t('SettingsUsers.locationAccess') },
                                ].map(level => (
                                    <Fragment key={level.id}>
                                        <StyledRadioGroup
                                            value={field.value}
                                            onChange={e =>
                                                setValue('accessLevel', e.target.value, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                })
                                            }
                                        >
                                            <FormControlLabel
                                                value={`${level.id}`}
                                                control={
                                                    <Radio
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.6)',
                                                            padding: '6px',
                                                            '&.Mui-checked': { color: '#1F4C5C' },
                                                        }}
                                                        disabled={isSubmitting}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                        {level.name}
                                                    </Typography>
                                                }
                                                disabled={isSubmitting}
                                            ></FormControlLabel>
                                        </StyledRadioGroup>
                                        {accessLevel === `${ACCESS_LEVEL.BRAND}` &&
                                            level.id === `${ACCESS_LEVEL.BRAND}` && (
                                                <Controller
                                                    control={control}
                                                    name="brands"
                                                    render={({ field, fieldState: { error }, ref }) => (
                                                        <MultiSelect
                                                            {...field}
                                                            ref={ref}
                                                            label={t('common.brands')}
                                                            onChange={value => {
                                                                setValue('brands', value, {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                });
                                                                setValue('locations', [], {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                });
                                                            }}
                                                            options={brandsOptions}
                                                            countable
                                                            required
                                                            error={error}
                                                            style={{
                                                                width: !isMobile ? 'calc(50% - 12px)' : '100%',
                                                                marginTop: '8px',
                                                            }}
                                                        />
                                                    )}
                                                />
                                            )}
                                        {accessLevel === `${ACCESS_LEVEL.LOCATION}` &&
                                            level.id === `${ACCESS_LEVEL.LOCATION}` && (
                                                <Controller
                                                    control={control}
                                                    name="locations"
                                                    render={({ field, fieldState: { error }, ref }) => (
                                                        <Autocomplete
                                                            {...field}
                                                            ref={ref}
                                                            label={t('common.locations')}
                                                            options={locationsOptions}
                                                            value={field.value}
                                                            isOptionEqualToValue={(option, value) =>
                                                                option.id === value.id
                                                            }
                                                            inputProps={{
                                                                style: {
                                                                    display:
                                                                        field?.value?.length > 0 ? 'none' : 'block',
                                                                },
                                                            }}
                                                            onChangeAutoComplete={items => {
                                                                setValue('locations', items.length ? items : [], {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                });
                                                                setValue('brands', [], {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                });
                                                            }}
                                                            groupMultiProps={{
                                                                getOptionLabel: option => option.label,
                                                            }}
                                                            required
                                                            error={error}
                                                            className="companies-autocomplete"
                                                        />
                                                    )}
                                                />
                                            )}
                                    </Fragment>
                                ))}
                            </div>
                        )}
                    />
                )}
            </Content>
        </Modal>
    );
};

export default EditModal;
